import { InViewport } from "@gik/core/components/InViewport";
import { useScrollToAnchor } from "@gik/core/hooks/UseScrollToAnchor";
import type WordpressSituation from "@gik/core/models/wordpress/WordpressSituation";
import { ErrorPageSection } from "@gik/core/pages/Error/ErrorPageSection";
import type { UIComponent } from "@gik/core/types/UI";
import { useBemCN } from "@gik/core/utils/bemBlock";
import { getAssetsUrl } from "@gik/core/utils/LinkUtils";
import withComponentErrorBoundary from "@gik/core/utils/withComponentErrorBoundary";
import { CreateClickedLocation } from "@gik/create/enums/CreateAnalyticsEnums";
import i18n from "@gik/i18n";
import type { AccordionItemType } from "@gik/ui-legacy/Accordion";
import { Accordion } from "@gik/ui-legacy/Accordion";
import { PageHeader } from "@gik/ui-legacy/gik/PageHeader";
import { PageSection } from "@gik/ui-legacy/gik/PageSection";
import { StartInkindCard } from "@gik/ui-legacy/gik/StartInkindCard";
import { TestimonialsCarouselSection } from "@gik/ui-legacy/gik/TestimonialsCarouselSection";
import { HTMLParser } from "@gik/ui-legacy/HTMLParser";
import { Image } from "@gik/ui-legacy/Image";
import React from "react";
import { useTranslation } from "react-i18next";
import { FeatureContent } from "../../components/FeatureContent";
import { MarketingNavSection } from "../../components/MarketingNav/MarketingNavSection";
import { translationKeys } from "./i18n/en";
import { TipsSamplePagesSection } from "./TipsSamplePagesSection";

export type TipsSamplePageServerProps = {
	situations: WordpressSituation[];
};

export type TipsSamplePageProps = {} & UIComponent;

function createAccordionItems(
	prefix: string,
	totalItems: number,
): AccordionItemType[] {
	return [...Array(totalItems)].map((_, index) => ({
		title: HTMLParser({
			rawHtml: i18n.t(translationKeys[`${prefix}${index + 1}Title`]),
		}),
		content: HTMLParser({
			rawHtml: i18n.t(translationKeys[`${prefix}${index + 1}Content`]),
		}),
	}));
}

const blockName = "tips-sample-page";

function TipsSamplePageComp(): React.ReactElement {
	const bem = useBemCN(blockName);
	const { t } = useTranslation();
	useScrollToAnchor();

	const organizersFAQItems: AccordionItemType[] = React.useMemo(
		() => createAccordionItems("organizersFAQ", 7),
		[],
	);
	const supportersFAQItems: AccordionItemType[] = React.useMemo(
		() => createAccordionItems("supportersFAQ", 4),
		[],
	);
	const recipientsFAQItems: AccordionItemType[] = React.useMemo(
		() => createAccordionItems("recipientsFAQ", 4),
		[],
	);

	return (
		<div {...bem()}>
			<InViewport height={160}>
				<PageHeader variant="neutral" decoration="foliage">
					<h1>{t(translationKeys.title)}</h1>
				</PageHeader>
			</InViewport>

			<InViewport height={180}>
				<PageSection {...bem("lead")} noPadBottom>
					<p className="gik-section-lead">
						{t(translationKeys.leadDescription)}
					</p>
				</PageSection>
			</InViewport>

			<InViewport height={1682}>
				<PageSection {...bem("section-features")}>
					<div {...bem("content")}>
						<FeatureContent
							variant="secondary"
							title={
								<div {...bem("feature_title_wrapper")}>
									<Image
										{...bem("feature_image")}
										src={[
											`${getAssetsUrl()}marketing/icons/individual.png`,
											`${getAssetsUrl()}marketing/icons/individual@2x.png 2x`,
										]}
									/>

									<div {...bem("feature_title")}>
										{t(translationKeys.organizersTitle)}
									</div>
								</div>
							}
							titleCentered
							description={t(translationKeys.organizersDescription)}
							headingLevel={2}
						>
							<Accordion headingLevel={3} items={organizersFAQItems} dividers />
						</FeatureContent>
						<div {...bem("foliage-mobile", "right")} />
						<FeatureContent
							variant="blue"
							title={
								<div {...bem("feature_title_wrapper")}>
									<Image
										{...bem("feature_image")}
										src={[
											`${getAssetsUrl()}marketing/icons/supporter.png`,
											`${getAssetsUrl()}marketing/icons/supporter@2x.png 2x`,
										]}
									/>

									<div {...bem("feature_title")}>
										{t(translationKeys.supportersTitle)}
									</div>
								</div>
							}
							titleCentered
							description={t(translationKeys.supportersDescription)}
							headingLevel={2}
						>
							<Accordion headingLevel={3} items={supportersFAQItems} dividers />
						</FeatureContent>
						<div {...bem("foliage-mobile", "left")} />
						<FeatureContent
							variant="danger"
							title={
								<div {...bem("feature_title_wrapper")}>
									<Image
										{...bem("feature_image")}
										src={[
											`${getAssetsUrl()}marketing/icons/love.png`,
											`${getAssetsUrl()}marketing/icons/love@2x.png 2x`,
										]}
									/>
									<div {...bem("feature_title")}>
										{t(translationKeys.recipientsTitle)}
									</div>
								</div>
							}
							titleCentered
							description={t(translationKeys.recipientsDescription)}
							headingLevel={2}
						>
							<Accordion headingLevel={3} items={recipientsFAQItems} dividers />
						</FeatureContent>
					</div>
				</PageSection>
			</InViewport>

			<InViewport height={797}>
				<TipsSamplePagesSection />
			</InViewport>

			<InViewport height={394}>
				<TestimonialsCarouselSection />
			</InViewport>

			<InViewport height={175} className="gik-start-inkind-card-wrapper">
				<StartInkindCard
					trackingId={CreateClickedLocation.TIPS_SAMPLE_PAGES_PAGE}
				/>
			</InViewport>

			<InViewport height={461}>
				<MarketingNavSection variant="neutral" />
			</InViewport>
		</div>
	);
}

export const TipsSamplePage = withComponentErrorBoundary(TipsSamplePageComp, {
	ErrorFallback: ErrorPageSection,
});
